import configs from "../configs";
import { uploadFile, uploadFileShort } from "../editor/utils/file";
import { getAuth0Token } from "../ui/utils";

const ACTIVATION_UPLOAD_OPTIONS = { useDefaultWorkspace: true, useWorkspaceAssets: true };

// Generate thumbnail from video file
const generateVideoThumbnail = (videoFile, time = 1) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const video = document.createElement("video");
    const context = canvas.getContext("2d");

    video.addEventListener("loadedmetadata", () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    });

    video.addEventListener("seeked", () => {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(resolve, "image/jpeg", 0.9);
    });

    video.addEventListener("error", reject);

    video.src = URL.createObjectURL(videoFile);
    video.currentTime = time;
  });
};

const getUploadUuid = uploadResult => {
  if (!uploadResult) return null;
  // uploadFile already returns uuid; do not derive from path
  return uploadResult.uuid || null;
};

const getNormalizedExt = (uploadResult, file) => {
  if (!uploadResult && !file) return "";
  const ext = (file?.name && file.name.split(".").pop()) || (uploadResult?.type ? uploadResult.type : null);
  return ext ? ext.replace(/^\./, "") : "";
};

const createResponseError = async (response, fallbackMessage) => {
  let message = fallbackMessage;
  let data = null;

  const rawText = await response.text().catch(() => "");
  if (rawText) {
    try {
      data = JSON.parse(rawText);
      message = data?.message || data?.error || fallbackMessage;
    } catch (e) {
      message = rawText || fallbackMessage;
    }
  }

  const error = new Error(message);
  error.status = response.status;
  if (data) {
    error.data = data;
  }

  return error;
};

export default class EventService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    this.baseUrl = `${configs.CUSTOM_API_URL}/events`;
    this.adminUrl = `${configs.CUSTOM_API_URL}/admin/events`;
    this.reservationsUrl = `${configs.CUSTOM_API_URL}/reservations`;
  }

  async getMaasEvents(query = "", page = 1, perPage = 20, signal = null) {
    const token = await getAuth0Token();
    const response = await fetch(this.adminUrl + "?search=" + query + "&page=" + page + "&per_page=" + perPage, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      },
      signal
    });

    if (!response.ok) {
      throw new Error("Failed to fetch activations");
    }

    const json = await response.json();

    return json;
  }

  async getEvents(query = "") {
    const token = await getAuth0Token();

    const response = await fetch(this.baseUrl + "/owned?search=" + query, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to fetch activations");
    }

    const json = await response.json();
    // If event does not have a background, set this to https://mudfoundation.blob.core.windows.net/nextrealitydigital/assets360/onland/events/production/metaverse-905ca0bb-32cc-4d07-a4f1-61db79aaaeea.jpg

    json.data.forEach(event => {
      if (!event.background && configs.ENVIRONMENT === "local") {
        event.background =
          "https://mudfoundation.blob.core.windows.net/nextrealitydigital/assets360/onland/events/production/metaverse-905ca0bb-32cc-4d07-a4f1-61db79aaaeea.jpg";
        event.thumbnail =
          "https://mudfoundation.blob.core.windows.net/nextrealitydigital/assets360/onland/events/production/metaverse-905ca0bb-32cc-4d07-a4f1-61db79aaaeea.jpg";
      }
    });

    return json.data;
  }

  async getPublicEvents({ queryParams = {}, signal } = {}) {
    const token = await getAuth0Token();
    const headers = {
      "Content-Type": "application/json",
      accept: "application/json"
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const params = new URLSearchParams();

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        return;
      }
      params.append(key, value);
    });

    if (!params.has("public")) {
      params.append("public", "true");
    }

    if (!params.has("published")) {
      params.append("published", "true");
    }

    const url = `${this.baseUrl}?${params.toString()}`;

    const response = await fetch(url, {
      signal,
      headers
    });

    if (!response.ok) {
      throw new Error("Failed to fetch public events");
    }

    const data = await response.json();

    return data.data ?? data;
  }

  async createEvent(event) {
    const { background, mobile_image, presentations, ...restEvent } = event;

    const appName = configs.APP_NAME || configs.REACT_APP_NAME;

    const token = await getAuth0Token();

    // Check if background is a video file
    const isVideo = background && background.type && background.type.startsWith("video/");

    const { id } = await uploadFile({
      file: background,
      folder: "assets360/" + appName + "/events",
      ...ACTIVATION_UPLOAD_OPTIONS
    });

    let mobileImageUuid = null;
    if (mobile_image instanceof File) {
      const uploadedMobile = await uploadFile({
        file: mobile_image,
        folder: "assets360/" + appName + "/events",
        asset_type: "event_mobile_background",
        ...ACTIVATION_UPLOAD_OPTIONS
      });
      const normalizedExt = getNormalizedExt(uploadedMobile, mobile_image);
      const uuid = getUploadUuid(uploadedMobile);
      mobileImageUuid = uuid && normalizedExt ? `${uuid}.${normalizedExt}` : uuid;
    } else if (typeof mobile_image === "string" && mobile_image.trim() !== "") {
      mobileImageUuid = mobile_image;
    }

    const payload = {
      ...restEvent,
      background: id
    };

    if (mobileImageUuid) {
      payload.mobile_image = mobileImageUuid;
    }

    // Handle presentation uploads (image/media + mobile_image)
    if (Array.isArray(presentations)) {
      const uploadedPresentations = await Promise.all(
        presentations.map(async presentation => {
          const next = { ...presentation };
          // Media/image upload (existing behavior)
          const mediaCandidate =
            next.media instanceof File ? next.media : next.image instanceof File ? next.image : null;
          if (mediaCandidate) {
            try {
              const { url } = await uploadFileShort({
                file: mediaCandidate,
                folder: "presentations",
                ...ACTIVATION_UPLOAD_OPTIONS
              });
              next.image = url;
            } catch (e) {
              console.warn("Failed to upload presentation image:", e);
            }
            delete next.media;
          }

          // Mobile image upload (uuid like events)
          if (next.mobile_image instanceof File) {
            try {
              const uploadedMobile = await uploadFile({
                file: next.mobile_image,
                folder: "assets360/" + appName + "/events",
                asset_type: "presentation_mobile_background",
                ...ACTIVATION_UPLOAD_OPTIONS
              });
              const normalizedExt = getNormalizedExt(uploadedMobile, next.mobile_image);
              const uuid = getUploadUuid(uploadedMobile);
              next.mobile_image = uuid && normalizedExt ? `${uuid}.${normalizedExt}` : uuid;
            } catch (e) {
              console.warn("Failed to upload presentation mobile image:", e);
              // If upload fails, drop mobile_image change to avoid blocking
              delete next.mobile_image;
            }
          }

          return next;
        })
      );
      payload.presentations = uploadedPresentations;
    }

    // If it's a video, generate and upload thumbnail
    if (isVideo) {
      try {
        const thumbnailBlob = await generateVideoThumbnail(background);
        const thumbnailFile = new File([thumbnailBlob], `${background.name}_thumbnail.jpg`, {
          type: "image/jpeg"
        });

        const { id: thumbnailId } = await uploadFile({
          file: thumbnailFile,
          folder: "assets360/" + appName + "/events",
          asset_type: "event_thumbnail",
          ...ACTIVATION_UPLOAD_OPTIONS
        });
        payload.thumbnail = thumbnailId;
      } catch (error) {
        console.warn("Failed to generate video thumbnail:", error);
        // Continue without thumbnail if generation fails
      }
    }

    const response = await fetch(this.baseUrl, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw await createResponseError(response, "Failed to create event");
    }

    const json = await response.json();

    return json.data;
  }

  async updateEvent(eventId, event) {
    const appName = configs.APP_NAME || configs.REACT_APP_NAME;
    const token = await getAuth0Token();

    let payload = { ...event };

    const hasNewMobileBackground = payload.mobile_image && payload.mobile_image instanceof File;

    if (hasNewMobileBackground) {
      const uploadedMobile = await uploadFile({
        file: payload.mobile_image,
        folder: "assets360/" + appName + "/events",
        asset_type: "event_mobile_background",
        ...ACTIVATION_UPLOAD_OPTIONS
      });
      const normalizedExt = getNormalizedExt(uploadedMobile, payload.mobile_image);
      const uuid = getUploadUuid(uploadedMobile);
      payload.mobile_image = uuid && normalizedExt ? `${uuid}.${normalizedExt}` : uuid;
    }

    // If background is a File/Blob, upload first and replace with id
    const hasNewBackground = payload.background && payload.background instanceof File;
    if (hasNewBackground) {
      // Check if background is a video file
      const isVideo = payload.background.type && payload.background.type.startsWith("video/");
      const backgroundFile = payload.background;

      const { id } = await uploadFile({
        file: backgroundFile,
        folder: "assets360/" + appName + "/events",
        ...ACTIVATION_UPLOAD_OPTIONS
      });
      payload = { ...payload, background: id };

      // Only generate thumbnail for new video uploads
      // This avoids regenerating thumbnails for existing videos unless explicitly replaced
      if (isVideo) {
        try {
          const thumbnailBlob = await generateVideoThumbnail(backgroundFile);
          const thumbnailFile = new File([thumbnailBlob], `${backgroundFile.name}_thumbnail.jpg`, {
            type: "image/jpeg"
          });

          const { id: thumbnailId } = await uploadFile({
            file: thumbnailFile,
            folder: "assets360/" + appName + "/events",
            asset_type: "event_thumbnail",
            ...ACTIVATION_UPLOAD_OPTIONS
          });
          payload.thumbnail = thumbnailId;
        } catch (error) {
          console.warn("Failed to generate video thumbnail:", error);
          // Continue without thumbnail if generation fails
        }
      } else {
        // If switching from video to image, clear thumbnail to avoid confusion
        payload.thumbnail = null;
      }
    } else {
      // Do not send background or thumbnail if not changed
      // Ensures we don't overwrite existing background/thumbnail with null or string values
      // eslint-disable-next-line no-unused-vars
      const { background, thumbnail, ...rest } = payload;
      payload = rest;
    }

    // Handle presenter image uploads (convert File -> hosted URL)
    if (Array.isArray(payload.presenters)) {
      const uploadedPresenters = await Promise.all(
        payload.presenters.map(async presenter => {
          const next = { ...presenter };
          const fileCandidate = next.image instanceof File ? next.image : null;
          if (fileCandidate) {
            try {
              const { url } = await uploadFileShort({
                file: fileCandidate,
                folder: "presenters",
                ...ACTIVATION_UPLOAD_OPTIONS
              });
              next.image = url;
            } catch (e) {
              // If upload fails, drop image change to avoid breaking entire save
              // eslint-disable-next-line no-console
              console.warn("Failed to upload presenter image:", e);
            }
          }
          return next;
        })
      );
      payload.presenters = uploadedPresenters;
    }

    // Handle presentation media uploads (convert File -> hosted URL)
    if (Array.isArray(payload.presentations)) {
      const uploadedPresentations = await Promise.all(
        payload.presentations.map(async presentation => {
          const next = { ...presentation };
          // Prefer 'media' field if provided by UI; fallback to image File
          const fileCandidate =
            next.media instanceof File ? next.media : next.image instanceof File ? next.image : null;
          if (fileCandidate) {
            try {
              const { url } = await uploadFileShort({
                file: fileCandidate,
                folder: "presentations",
                ...ACTIVATION_UPLOAD_OPTIONS
              });
              next.image = url;
            } catch (e) {
              // If upload fails, drop image change to avoid breaking entire save
              // eslint-disable-next-line no-console
              console.warn("Failed to upload presentation image:", e);
            }
            // Remove file-only fields before sending JSON
            delete next.media;
          }

          // Mobile image upload for presentation
          if (next.mobile_image instanceof File) {
            try {
              const uploadedMobile = await uploadFile({
                file: next.mobile_image,
                folder: "assets360/" + appName + "/events",
                asset_type: "presentation_mobile_background",
                ...ACTIVATION_UPLOAD_OPTIONS
              });
              const normalizedExt = getNormalizedExt(uploadedMobile, next.mobile_image);
              const uuid = getUploadUuid(uploadedMobile);
              next.mobile_image = uuid && normalizedExt ? `${uuid}.${normalizedExt}` : uuid;
            } catch (e) {
              console.warn("Failed to upload presentation mobile image:", e);
              delete next.mobile_image;
            }
          }

          return next;
        })
      );
      payload.presentations = uploadedPresentations;
    }

    const response = await fetch(`${this.baseUrl}/${eventId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw await createResponseError(response, "Failed to update event");
    }

    const json = await response.json();
    return json.data;
  }

  async deleteEvent(eventId) {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/${eventId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to delete event");
    }

    return response.json();
  }

  async getEventBySlug(slug) {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/${slug}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to fetch activation");
    }

    const json = await response.json();

    if (!json.data.background && configs.ENVIRONMENT === "local") {
      json.data.background = {
        url: "https://mudfoundation.blob.core.windows.net/nextrealitydigital/assets360/onland/events/production/metaverse-905ca0bb-32cc-4d07-a4f1-61db79aaaeea.jpg"
      };
      json.data.thumbnail =
        "https://mudfoundation.blob.core.windows.net/nextrealitydigital/assets360/onland/events/production/metaverse-905ca0bb-32cc-4d07-a4f1-61db79aaaeea.jpg";
    }

    return json.data;
  }

  // Reservations API
  async holdReservation({ eventSlug, presentationSlugs = [], guestId, returnUrl }) {
    const token = await getAuth0Token();
    const url = `${this.reservationsUrl}/hold`;
    const body = {
      event_slug: eventSlug,
      guest_id: guestId || null,
      return_url: returnUrl || window.location.href
    };
    if (Array.isArray(presentationSlugs) && presentationSlugs.length > 0) {
      body.presentation_slugs = presentationSlugs;
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      let errorMessage = response.statusText;
      try {
        const json = JSON.parse(text);
        errorMessage = json.message || json.error || errorMessage;
      } catch {
        // If not JSON, use the text as-is
        errorMessage = text || errorMessage;
      }
      // Make "Unauthorized" more user-friendly
      if (errorMessage === "Unauthorized") {
        errorMessage = "Please sign in to register for this event.";
      }
      throw new Error(`Unable to register for event. ${errorMessage}`);
    }

    return response.json();
  }

  async claimReservation({ bookingUuid, guestId }) {
    const token = await getAuth0Token();
    const url = `${this.reservationsUrl}/claim`;
    const body = { booking_uuid: bookingUuid, guest_id: guestId || null };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      let errorMessage = response.statusText;
      try {
        const json = JSON.parse(text);
        errorMessage = json.message || json.error || errorMessage;
      } catch {
        // If not JSON, use the text as-is
        errorMessage = text || errorMessage;
      }
      // Make "Unauthorized" more user-friendly
      if (errorMessage === "Unauthorized") {
        errorMessage = "Please sign in to complete your registration.";
      }
      throw new Error(`Unable to complete registration. ${errorMessage}`);
    }

    return response.json();
  }

  // Get events with users
  async getEventsWithUsers() {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/owned-with-users`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to fetch activations with users");
    }

    const json = await response.json();
    return json.data;
  }

  async getEventMetrics(slug, startDate, endDate, abortSignal = null) {
    const token = await getAuth0Token();

    const url = `${this.baseUrl}/${slug}/metrics?start_date=${startDate}&end_date=${endDate}`;

    const response = await fetch(url, {
      signal: abortSignal,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to fetch activation metrics");
    }

    const json = await response.json();
    return json;
  }

  async submitEventFeedback(slug, feedback) {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/${slug}/feedback`, {
      method: "POST",
      body: JSON.stringify(feedback),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to submit activation feedback");
    }

    return response.json();
  }

  async getEventFeedback(slug) {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/${slug}/feedback`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to fetch event feedback");
    }

    const json = await response.json();
    return json;
  }

  // Check-in API
  async verifyCheckin({ slug, token }) {
    const authToken = await getAuth0Token();
    const url = `${this.baseUrl}/${slug}/checkin/verify/${token}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    const contentType = response.headers.get("content-type") || "";
    const parse = async () =>
      contentType.includes("application/json") ? await response.json() : await response.text();
    const data = await parse().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(data?.message || "Failed to verify check-in token");
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  async redeemCheckin({ slug, token }) {
    const authToken = await getAuth0Token();
    const url = `${this.baseUrl}/${slug}/checkin/redeem`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
        accept: "application/json"
      },
      body: JSON.stringify({ token })
    });

    const contentType = response.headers.get("content-type") || "";
    const parse = async () =>
      contentType.includes("application/json") ? await response.json() : await response.text();
    const data = await parse().catch(() => ({}));

    console.log("Redeem check-in response:", response.ok, response.status, data);

    if (!response.ok) {
      const error = new Error(data?.message || "Failed to redeem check-in token");
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  async previewEventEmail(slug, { type, variant, email } = {}) {
    const token = await getAuth0Token();

    const url = `${this.baseUrl}/${slug}/emails/preview`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      },
      body: JSON.stringify({ type, variant, email })
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Failed to send preview email (${response.status}). ${text || response.statusText}`);
    }

    return response.json().catch(() => ({}));
  }

  async getTrashedEvents(query = "") {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/owned/trashed?search=${query}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to fetch trashed events");
    }

    const json = await response.json();
    return json.data;
  }

  async restoreEvent(slug) {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/${slug}/restore`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to restore event");
    }

    const json = await response.json();
    return json.data;
  }

  async forceDeleteEvent(slug) {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/${slug}/force`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to permanently delete event");
    }

    return true;
  }

  async cloneEvent(slug, cloneOptions) {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/${slug}/clone`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      },
      body: JSON.stringify(cloneOptions)
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Failed to clone event (${response.status}). ${text || response.statusText}`);
    }

    const json = await response.json();
    return json.data;
  }

  async claimInvitation({ token, slug }) {
    const authToken = await getAuth0Token();

    const response = await fetch(`${configs.CUSTOM_API_URL}/invitations/claim`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
        accept: "application/json"
      },
      body: JSON.stringify({ token, event_slug: slug })
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Failed to claim invitation (${response.status}). ${text || response.statusText}`);
    }

    return response.json();
  }

  async toggleFeatured(slug) {
    const token = await getAuth0Token();

    const response = await fetch(`${this.baseUrl}/${slug}/featured`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Failed to toggle featured");
    }

    return response.json();
  }
}
